import { useEffect, useState } from 'react';
import { ILead } from 'src/interfaces/lead';
import { isChannelsModeArea } from 'src/modules/conversations/channels';
import {
    getLeadChannelConversation,
    TLeadChannelConversation,
} from 'src/modules/conversations/api';
import {
    getLeadStoreArea,
    getLeadStoreAreaName,
} from 'src/modules/assistant/helpers/getLeadStoreArea';

interface TLeadChannelConversationState {
    conversation: TLeadChannelConversation;
    loading: boolean;
}

const EMPTY_STATE: TLeadChannelConversationState = Object.freeze({
    conversation: null,
    loading: false,
});

/**
 * `WarboxConversation` (OmniChannel) of the lead when its area is in `channels`
 * mode; null without a request otherwise. Reloads when the lead or its area changes.
 * A failed lookup counts as no conversation, so only templates are offered.
 */
export const useLeadChannelConversation = (
    lead: ILead | null
): TLeadChannelConversationState => {
    const [state, setState] =
        useState<TLeadChannelConversationState>(EMPTY_STATE);
    const leadId = lead?._id || null;
    const areaName = lead ? getLeadStoreAreaName(lead) : null;
    const isChannelsMode = lead
        ? isChannelsModeArea(getLeadStoreArea(lead))
        : false;

    useEffect(() => {
        if (!leadId || !areaName || !isChannelsMode) {
            setState(EMPTY_STATE);
            return undefined;
        }
        // axios 0.21 has no `signal`: ignore responses for a previous lead
        let isCurrent = true;
        setState({ conversation: null, loading: true });
        getLeadChannelConversation(leadId, areaName)
            .catch(() => null)
            .then((conversation) => {
                if (isCurrent) setState({ conversation, loading: false });
            });
        return () => {
            isCurrent = false;
        };
    }, [leadId, areaName, isChannelsMode]);

    return state;
};
